import { Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, LinearProgress, Stack, TextField, Tooltip, Typography, useTheme } from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import { useKeycloak } from '@react-keycloak/web';
import { useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import dayjs from 'dayjs';
import { useAtom, useAtomValue } from 'jotai';
import { selectAtom } from 'jotai/utils';
import { isEqual, noop, rest } from 'lodash';
import * as React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { artifactAtom } from '../../../stores/jotai/artifacts/artifacts';
import { baseAtom, formatStatus, selectInterview } from '../../../stores/jotai/base';
import { discoverySessionAtom } from '../../../stores/jotai/discovery-session';
import { interviewsAtom } from '../../../stores/jotai/interviews/interviews';
import { hasCanvasWriteAccess } from '../../../stores/jotai/permissions';
import { useCreateInterview, useUpdateInterview } from '../../../tanstackQuery/queries/interviews';
import { useGetTemplates } from '../../../tanstackQuery/queries/templates';
import AlertDialog from '../../app/alert-dialog';
import IwButton from '../../app/IwButton';
import { DropdownV2 } from '../../assorted/dropdown-v2';
const SAVE_BUTTON_IDS = {
    SAVE: 1,
    SAVE_AND_OPEN: 2,
};
const teamAtom =  selectAtom(baseAtom, (base) => base.team);
const roleSnapshotAtom = selectAtom(baseAtom, (base) => base.roleSnapshot);

export default function MaterialInterviewDetails(props) {
    const {
        interview: interviewFromProps,
        showSaveAndOpenButton,
        existingInterview,
        interviewModalClosed,
        requestModalClose,
        close,
        setInterviewModalClosed,
        setBase,
        zoomToggleEventPayload,
        handleToggleZoomEvent
    } = props;

    const discoverySession = useAtomValue(discoverySessionAtom);
    const [hasDraftChange, setHasDraftChange] = useState<boolean>(false);
    const [selectTemplate, setSelectTemplate] = useState<boolean>(false);
    const [checkInterviewee, setCheckInterviewee] = useState<boolean>(false);
    const [showCancelConfirmationModal, setShowCancelConfirmationModal] =
        useState<boolean>(false);
    const [clickedButtonID, setClickedButtonID] = useState<null | number>(null);
    const { keycloak } = useKeycloak()
    const [roleSnapshot] = useAtom(roleSnapshotAtom);
 
    const [team] = useAtom(teamAtom);
    const theme = useTheme();
    const [interviews,setInterviews] = useAtom(interviewsAtom);
    const [artifacts, setArtifacts] = useAtom(artifactAtom);
    const [state, setState] = useState({
        selectedTemplate: { value: "No template", key: undefined },
        loadingMetadata: true,
        metaData: {},
        savingData: false,
        customFieldName: '',
        speakerCache: 2,
        isInterviewGiven: false,
        _interviewee: ""
    });
    const [isSaving, setIsSaving] = useState(false);
    const [_interview, setInterview] = useState({
        empty: true,
        status: "",
        templateID: "",
        created: null,
    });
    
    const [showConfirmationModal, setShowConfirmationModal] = useState<boolean>(false);
    const [isCheckingTemplateChange, setIsCheckingTemplateChange] =
        useState<boolean>(false);
    const canEditCanvas = hasCanvasWriteAccess(roleSnapshot);
    const navigate = useNavigate()

    const { data: templateDetails } =
      useGetTemplates(
        { accessToken: keycloak.token },
        { groupID: team?.groupID },
        { enabled: !!team?.groupID }
      );

    const client = useQueryClient();
    const { mutateAsync: handleUpdateInterview } = useUpdateInterview(
      { accessToken: keycloak?.token },
      {
        onSuccess() {
          client.invalidateQueries({ queryKey: ["interviews"] });
        }
      }
    );

    const { mutateAsync: handleCreateInterview } = useCreateInterview(
      { accessToken: keycloak?.token },
      {
        onSuccess(res) {
          client.invalidateQueries({ queryKey: ["interviews"] });
          if (
            handleToggleZoomEvent &&
            zoomToggleEventPayload &&
            res?.interviewID
          ) {
            handleToggleZoomEvent({
              ...zoomToggleEventPayload,
              appInterviewID: res?.interviewID
            });
          }
        }
      }
    );
    
    const checkAndHandleTemplateChange = () => {
        if (
            existingInterview &&
            interviewFromProps?.interviewID &&
            _interview?.templateID &&
            interviewFromProps?.templateID !== _interview?.templateID
        ) {
            const interviewID = interviewFromProps?.interviewID;
            const noteArtifactID = artifacts.note?.[interviewID]?.artifactID;

            setIsCheckingTemplateChange(true);

            axios
                .get(
                    `${process.env.NEXT_PUBLIC_LINX_URL}/note/${noteArtifactID}/edit/permisson`,
                    {
                        withCredentials: false,
                        headers: {
                            Authorization: "Bearer " + keycloak.token,
                        },
                    }
                )
                .then((response) => {
                    if (response.status === 200) {
                        if (!response.data?.canEdit) {
                            setShowConfirmationModal(true);
                        }
                    }
                })
                .catch((error) => {
                    if (error.response.status === 403) {
                        setShowConfirmationModal(true);
                    }
                })
                .finally(() => {
                    setIsCheckingTemplateChange(false);
                });
        }
    };

    const revertTemplateChange = () => {
        setInterview((prevInterview) => ({
            ...prevInterview,
            templateID: interviewFromProps?.templateID,
        }));
        setShowConfirmationModal(false);
    }

    React.useEffect(() => {
        // make sure it has the minimal meta fields then inject it
        let dataFields = {
            ...interviewFromProps,
        };

        const mFields = getDefaultMetaFields();

        for (let i = 0; i < mFields?.length; i++) {
            const fieldTitle = `meta_${mFields[i]}`;

            if (dataFields[fieldTitle] === undefined) {
                dataFields[fieldTitle] = "";
            }
        }
        if (
        (!dataFields.email || dataFields.email === "") &&
        zoomToggleEventPayload?.email?.length > 0
        ) {
        dataFields.email = zoomToggleEventPayload?.email?.join(",");
        }
        if (
        (!dataFields.type || dataFields.type === "") &&
        zoomToggleEventPayload?.topic
        ) {
        dataFields.type = zoomToggleEventPayload?.topic;
        }
        if (
        (!dataFields.created || dataFields.created === null) &&
        zoomToggleEventPayload?.startTime
        ) {
        dataFields.created = zoomToggleEventPayload?.startTime;
        }
        setInterview(dataFields);
    }, [
        interviewFromProps.communication,
        interviewFromProps.company,
        interviewFromProps.created,
        interviewFromProps.email,
        interviewFromProps.templateID,
        interviewFromProps.meta_City,
        interviewFromProps["meta_Company Size"],
        interviewFromProps.interviewee,
        interviewFromProps.meta_State,
        interviewFromProps.phone,
        interviewFromProps.title,
        interviewFromProps.type,
        zoomToggleEventPayload
    ]);

    const handleUserKeyPress = (e: any) => {
        if (e.key === "Enter") {
            // handle submit
            if (isValid() && !isCheckingTemplateChange && canEditCanvas) {
                if(showSaveAndOpenButton) handleSaveButtonClick()
                else handleSaveAndOpenButtonClick()
            }
        }
    };

    useEffect(() => {
        window.addEventListener('keydown', handleUserKeyPress);

        return () => {
            window.removeEventListener('keydown', handleUserKeyPress);
        };
    }, [_interview]);

    const save = (cb?, onSuccess?) => {
        let speakerCache = undefined;
        let templateKey = undefined;

        try {
            speakerCache = state.speakerCache;
            templateKey = state.selectedTemplate.key;
        } catch (e) {}

        if (existingInterview) {
            handleUpdateInterview({
                ..._interview,
                status: formatStatus(_interview ? _interview.status : 'draft'),
                speakers: speakerCache,
                groupID: team.groupID,
                sessionID: discoverySession.sessionID
            }).then(interview => {
                cb();
                onSuccess(interview);
                client.invalidateQueries({ queryKey: ["artifacts"] })
            })
        } else {
            handleCreateInterview({
                ..._interview,
                speakers: speakerCache,
                status: formatStatus(_interview ? _interview.status : 'draft'),
                groupID: team.groupID,
                sessionID: discoverySession.sessionID

            }).then(interview => {
                cb();
                onSuccess(interview);
                client.invalidateQueries({ queryKey: ["artifacts"] })
            });
        }
    };

    const getDefaultMetaFields = () => {
        return ['City', 'State', 'Company Size']
    };

    const templateList = React.useMemo(() => {
        const teamTemplates = templateDetails?.templates || [];
        const sortedTemplates = teamTemplates?.sort((a, b) => new Date(b?.created)?.getTime() - new Date(a?.created)?.getTime());

        return [
            // { value: "Templete list", key: null },
             ...sortedTemplates?.map((template) => ({
                value: template?.name,
                key: template?.templateID,
            })),
        ];
    }, [templateDetails?.templates]);

    const labelMap = (key) => {
        if (key === "interviewee") {
            return "Interviewee";
        }

        if (key === "created") {
            return "Interview Date";
        }

        if (key === "company") {
            return "Company";
        }

        if (key === "title") {
            return "Title";
        }

        if (key === "email") {
            return "Email";
        }

        if (key === "phone") {
            return "Phone";
        }

        if (key === "communication") {
            return "Communication";
        }

        if (key === "type") {
            return "Interview Topic";
        }

        if (key === "templateID") {
            return "Interview Template";
        }

        return key;
    }

    const isKeyRequired = (key) => {
        if (key === 'interviewee') {
            return true;
        }

        return false;
    }

    const renderField = (key) => {
        const int = _interview;
        let val = int?.[key];
        let lab = labelMap(key);
        let required = isKeyRequired(key);

        if (lab.includes("meta_")) {
            lab = lab.replace("meta_", "");
        }
        let isEmptyValue = val === undefined || val === "" || val === null;
    
        // handle each key/input type
        if (key === 'templateID') {
            const selectedTemplate = templateDetails?.templates?.find(
                (template) => template?.templateID === val
            );

            return (
                <Stack style={{ paddingBottom: "6px", position: "relative" }}>
                    {isCheckingTemplateChange && (
                        <Box
                            sx={{
                                position: "absolute",
                                bgcolor: "#f2f2f2",
                                right: "8px",
                                top: "8px",
                                zIndex: 1,
                            }}
                        >
                            <CircularProgress size={20} />
                        </Box>
                    )}
                    {
                       selectTemplate && <Typography color="error" fontSize="0.75rem">Choose interview template</Typography>
                    }
                   <DropdownV2
                        placeholder="Template List *"
                        selectWillClose={true}
                        disabled={!canEditCanvas || isCheckingTemplateChange}
                        onChange={(_, e) => {
                            setSelectTemplate(false); 
                            setInterview({
                                ...int,
                                [key]: e,
                            });
                        }}
                        selected={
                            selectedTemplate
                                ? {
                                      value: selectedTemplate?.name,
                                      key: selectedTemplate?.templateID,
                                  }
                                : (Object?.keys(interviewFromProps)?.length) ? { value: "Empty Template", key: null } : null
                        }
                        items={[{ value: "Empty Template", key: null }, ...templateList]}
                        theme={theme}
                    />
                </Stack>
            );
        }
        else if (key === 'communication') {
            const commItems = [
                { key: 'In Person', value: 'In Person' },
                { key: 'By Phone', value: 'By Phone' },
                { key: 'Internet Call', value: 'Internet Call' }
            ]
            return <div style={{ paddingBottom: '6px' }}>
                <DropdownV2
                    placeholder="Communication"
                    selectWillClose={true}
                    disabled={!canEditCanvas}
                    selected={val}
                    onChange={(_, e) => {
                        setInterview({
                            ...int,
                            [key]: e
                        })
                    }}
                    items={commItems}
                    theme={theme}
                />
            </div>
        }
        else if (key === 'created') {
            const datePickerHeight = '40px';
            return <div style={{ paddingBottom: '6px' }}>
                <DatePicker 
                    value={dayjs(val).isValid() ? dayjs(val) : dayjs()}
                    disabled={!canEditCanvas}
                    onChange={(e) => {
                        setInterview({
                            ...int,
                            [key]: dayjs(e).toString()
                        })
                    }}
                    sx={{
                        height: datePickerHeight, '& .MuiInputBase-root': {
                            height: datePickerHeight
                        }
                    }} />
            </div>
        }
        else {

            let autoFocus = false;

            if (key === 'interviewee' && isEmptyValue) {
                autoFocus = true;
            }

            let requiredAndEmpty = false;

            if (required) {
                if (val === undefined || val === null || val === "") {
                    requiredAndEmpty = true;
                }
            }
            // fallback to the normal text field
            return (
                <>
                    {
                        key === 'interviewee' && checkInterviewee && <Typography color="error" fontSize="0.75rem">You must include the interviewee's name</Typography>
                    }
                    <TextField autoFocus={autoFocus} required={required} sx={{ paddingBottom: '6px', ".MuiInputBase-root": {
                        borderBottom: requiredAndEmpty ? "2px solid " + theme.custom.pendingChangeIndicatorColor : undefined
                        } }} size="small" label={lab} value={val} disabled={!canEditCanvas} onChange={(e) => {
                            if(key === 'interviewee'){
                                setCheckInterviewee(false); 
                            }
                            setInterview({
                                ...int,
                                [key]: e.target.value
                            })
                    }} />
                </>
            )           
        }
    }

    if (_interview === undefined) {
        return <LinearProgress />
    }

    let renderedFields = [];
    let renderedFieldsRight = [];

    let dataFields = Object.keys(_interview);

    const includeFields = ['templateID', 'interviewee', 'created', 'type', 'company', 'title', 'email', 'phone', 'communication']
    const ignoreFields = ['userID', 'owner', 'interviewID', 'groupID', 'id', 'cre'];
    const renderAditionalFieldsFromInterviewProps = false;

    // render all the essential fields first
    for (let i = 0; i < includeFields.length; i++) {
        renderedFields.push(
            renderField(includeFields[i])
        )
    }

    // render any extra fields in the interview
    for (let i = 0; i < dataFields?.length; i++) {
        if (includeFields.includes(dataFields[i]) === false && ignoreFields.includes(dataFields[i]) === false) {

            if (dataFields[i].includes("meta_")) {
                renderedFieldsRight.push(renderField(dataFields[i]));
            }
            else {
                if (renderAditionalFieldsFromInterviewProps) {
                    renderedFields.push(
                        renderField(dataFields[i])
                    )
                }
            }
        }
    }

    const isValid = () => {
        if (_interview === undefined) {
            return false;
        }

        if(_interview?.templateID === undefined){
            setSelectTemplate(true); 
        }
        if(_interview?.interviewee === undefined || _interview?.interviewee === ""){
            setCheckInterviewee(true);
            }

        const isValid = dayjs(_interview?.created).isValid();

        //@ts-ignore
        if (_interview.interviewee === undefined || _interview.interviewee === "" || !isValid  || _interview?.templateID === undefined) {
            return false;
        }

        return true;
    }

    const handleSaveButtonClick = () => {
        setClickedButtonID(SAVE_BUTTON_IDS.SAVE);
        setIsSaving(true);

        if (isValid() && !isCheckingTemplateChange && canEditCanvas) {
            save(noop, () => {
                setIsSaving(false);
                props.close();
            });
        }
    };

    const openCreatedInterview = (interview) => {
        const url = `/discovery/`;

        const allInterviews=[...interviews,interview]
        selectInterview(setBase, interview.interviewID, allInterviews);
        navigate(url)
    };

    const handleSaveAndOpenButtonClick = () => {
        if (showSaveAndOpenButton) {
          setClickedButtonID(SAVE_BUTTON_IDS.SAVE_AND_OPEN);
        } else {
          setClickedButtonID(SAVE_BUTTON_IDS.SAVE);
        }
    
        setIsSaving(true);
    
        if (isValid() && !isCheckingTemplateChange && canEditCanvas) {
          save(noop, (response) => {
            setIsSaving(false);
            openCreatedInterview(response);
            props.close();
          });
        }

        if(props?.fromInterviewDrawer && props?.setIsOpen){
            props.setIsOpen(false)
        }
      };

    const detectDraftChange = () => {
        interviewFromProps.meta_City = interviewFromProps.meta_City ?? "";
        interviewFromProps["meta_Company Size"] = interviewFromProps["meta_Company Size"] ?? "";
        interviewFromProps.meta_State = interviewFromProps.meta_State ?? "";
        const hasChange = !isEqual(_interview, interviewFromProps);
        setHasDraftChange(hasChange);
    };

    const handleInterviewModalClose = () => {
        if(interviewModalClosed) {
            if(hasDraftChange) {
                setShowCancelConfirmationModal(true);
            }
            else {
                close();
            }
        }
    }

    useEffect(() => {
        checkAndHandleTemplateChange();
    }, [_interview?.templateID, interviewFromProps?.templateID]);

    useEffect(() => {
        detectDraftChange();
    }, [_interview]);

    useEffect(() => {
        handleInterviewModalClose()
    }, [interviewModalClosed]);

    return <Stack>
        <Dialog
            className="confirmation-dialog"
            open={showCancelConfirmationModal}
            sx={{ zIndex: theme.custom.dialogZIndex }}
            onClose={() => {
                setShowCancelConfirmationModal(false);
            }}
        >
            <DialogTitle>Discard Unsaved Interview Data?</DialogTitle>
            <DialogContent>
                <DialogContentText>
                    Cancelling this will discard all the unsaved data? Are
                    you sure to proceed?
                </DialogContentText>
            </DialogContent>
            <DialogActions sx={{ margin: "0px 16px 8px 0px" }}>
                <Button
                        variant="contained"
                        autoFocus
                        onClick={() => {
                            setShowCancelConfirmationModal(false);
                            setInterviewModalClosed(false);
                        }}
                    >
                        No
                </Button>
                <Button
                    variant="outlined"
                    onClick={() => {
                        close();
                        setInterviewModalClosed(false);
                        setShowCancelConfirmationModal(false);
                    }}
                >
                    Yes
                </Button>
            </DialogActions>
        </Dialog>
        <AlertDialog
            title="Want to Change Template?"
            description="Saving the interview with a different template will remove all the text from the note. Do you want to proceed?"
            open={showConfirmationModal}
            onClose={() => {
                setShowConfirmationModal(false);
            }}
            confirmButton={
                <IwButton
                    variant="contained"
                    onClick={() => {
                        setShowConfirmationModal(false);
                    }}
                >
                    Yes
                </IwButton>
            }
            abortButton={
                <IwButton
                    variant="outlined"
                    onClick={revertTemplateChange}
                >
                    Cancel
                </IwButton>
            }
        />
        <Stack direction="row">
            <Stack spacing={0.5} sx={{ width: '49%', marginRight: '2%' }}>
                {renderedFields}
            </Stack>
            <Stack spacing={0.5} sx={{ width: '49%' }}>
                {renderedFieldsRight}
            </Stack>
        </Stack>

        <Stack justifyContent="flex-end" direction="row" sx={{ marginTop: '10px', width: '100%' }}>
            <Button variant="outlined" onClick={() => {
                requestModalClose ? requestModalClose() : close();
            }} sx={{ marginRight: '10px' }}>Cancel</Button>
            {showSaveAndOpenButton ? (
                <IwButton
                    variant="outlined"
                    onClick={ () =>  isValid() ? handleSaveAndOpenButtonClick() : null}
                    loading={isSaving && clickedButtonID === SAVE_BUTTON_IDS.SAVE_AND_OPEN}
                    disabled={
                        !canEditCanvas ||
                        isCheckingTemplateChange ||
                        (isSaving && clickedButtonID === SAVE_BUTTON_IDS.SAVE_AND_OPEN)
                    }
                    sx={{
                        marginRight: "10px",
                    }}
                >
                    Save & Open
                </IwButton>
            ) : null}
            <IwButton
                variant="contained"
                onClick={ () => 
                    isValid() 
                     ? showSaveAndOpenButton || handleToggleZoomEvent 
                     ? handleSaveButtonClick()
                     : handleSaveAndOpenButtonClick()
                    : null
                }
                disabled={
                    !canEditCanvas ||
                    isCheckingTemplateChange ||
                    (isSaving && clickedButtonID === SAVE_BUTTON_IDS.SAVE)
                }
                loading={isSaving && clickedButtonID === SAVE_BUTTON_IDS.SAVE}
            >
                Save
            </IwButton>
        </Stack>
    </Stack>

}


